import { useState, useCallback, useRef } from 'react'
import type { FormData, Gateways } from '@/types/apply'
import { getAttribution } from '@/lib/attribution'
import { readConsent } from '@/lib/consent'
import { track, generateEventId } from '@/lib/analytics'
import { refreshCsrfToken } from '@/lib/csrf'

// Module-level so it's stable inside useCallbacks (no analytics-driven deps).
function trackPaymentFailed(provider: string, errorMessage: string, token?: string | null): void {
  track('payment_failed', {
    data: { provider, error_message: errorMessage, application_token: token ?? undefined },
  })
}

type PaymentStatus = 'idle' | 'submitting' | 'redirecting' | 'modal_open' | 'verifying' | 'success' | 'error'

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

function buildApplicationPayload(formData: FormData) {
  return {
    application: {
      personal_info: {
        first_name: formData.personal_info.first_name.trim(),
        last_name: formData.personal_info.last_name.trim(),
        email: formData.personal_info.email.trim().toLowerCase(),
        phone: `+${formData.personal_info.phone_dial_code}${formData.personal_info.phone.trim()}`,
        website: formData.personal_info.website.trim()
      },
      address: {
        street_address: formData.address.street_address.trim(),
        address_line_2: formData.address.address_line_2.trim(),
        city: formData.address.city.trim(),
        state: formData.address.state.trim(),
        postal_code: formData.address.postal_code.trim(),
        country: formData.address.country
      },
      role_info: {
        role: formData.role_info.role,
        artist_name: formData.role_info.artist_name.trim(),
        label_name: formData.role_info.label_name.trim(),
        artists_under_management: formData.role_info.artists_under_management.trim()
      },
      demo_links: formData.demo_links.filter(l => l.trim()),
      social_media: {
        instagram: formData.social_media.instagram.trim(),
        tiktok: formData.social_media.tiktok.trim(),
        twitter: formData.social_media.twitter.trim(),
        facebook: formData.social_media.facebook.trim()
      },
      subscription: {
        plan: formData.subscription.plan,
        billing_cycle: formData.subscription.billing_cycle,
        currency: formData.subscription.currency
      },
      // Acceptance booleans — server treats every absent or falsy value as
      // 'not agreed', so it's safe to send false explicitly during the
      // disabled-submit window.
      agreements: {
        terms_of_service: formData.agreements.terms_of_service,
        ai_music_policy:  formData.agreements.ai_music_policy,
        reapply_policy:   formData.agreements.reapply_policy
      },
      invitation_code: formData.subscription.invitation_code.trim() || 'NA'
    }
  }
}

let razorpayLoaded = false

function loadRazorpaySdk(): Promise<void> {
  if (razorpayLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => { razorpayLoaded = true; resolve() }
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'))
    document.head.appendChild(script)
  })
}

export function usePayment(
  gateways: Gateways,
  razorpayKeyId: string | null
) {
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle')
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [applicationToken, setApplicationToken] = useState<string | null>(null)
  const submittingRef = useRef(false)

  const submitApplication = useCallback(async (formData: FormData, gateway: string, turnstileToken?: string) => {
    if (submittingRef.current) return
    submittingRef.current = true
    setPaymentStatus('submitting')
    setPaymentError(null)

    try {
      const { first_touch, last_touch } = getAttribution()
      // Dedup id shared with the server: the controller echoes it into the
      // CAPI Lead ping so Meta counts the browser + server copies of this
      // submission as one conversion.
      const analyticsEventId = generateEventId()
      const body = JSON.stringify({
        ...buildApplicationPayload(formData),
        gateway,
        'cf-turnstile-response': turnstileToken,
        first_touch,
        last_touch,
        marketing_consent: readConsent().marketing,
        analytics_event_id: analyticsEventId
      })
      const send = (token: string) => fetch('/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body
      })

      let res = await send(getCsrfToken())
      let data = await res.json()

      // A STALE TOKEN, ONCE. A phone that killed the browser while the applicant
      // was in Gmail fetching the code restores this form with a token whose
      // session cookie is gone; the server refuses before doing anything and says
      // so (`csrf_expired`). Nothing was created and the Turnstile token was never
      // checked, so the same submission is resent with a fresh token — once, and
      // only on that answer, never on a real validation error.
      if (res.status === 422 && data.csrf_expired) {
        const fresh = await refreshCsrfToken()
        if (fresh) {
          res = await send(fresh)
          data = await res.json()
        }
      }

      // Server detected an existing application for this email — bounce the
      // user straight to their status page instead of showing a generic
      // error. The OTP modal blocks duplicates client-side, but we hit this
      // branch when the user got past the modal (stale tab, refresh, etc.).
      if (!res.ok && data.duplicate && data.redirect_to) {
        window.location.href = data.redirect_to
        return
      }

      if (!res.ok) {
        const message = data.errors?.join(', ') || data.error || 'Application submission failed'
        setPaymentStatus('error')
        setPaymentError(message)
        trackPaymentFailed(gateway, message)
        submittingRef.current = false
        return
      }

      // The application now exists — this is the submission conversion, fired
      // here (not at payment success) so Stripe-redirect and stalled-payment
      // applicants still count as leads. Same event id as the server CAPI ping.
      track('application_submitted', {
        metaEvent: ['Lead', 'SubmitApplication'],
        conversion: 'application_submitted',
        eventId: analyticsEventId,
        data: {
          application_token: data.token,
          plan_slug: formData.subscription.plan,
          profile_type: formData.role_info.role || undefined,
          country: formData.address.country || undefined
        }
      })

      if (data.requires_redirect) {
        // Stripe: redirect to checkout
        setPaymentStatus('redirecting')
        setApplicationToken(data.token)
        window.location.href = data.checkout_url
        return
      }

      if (data.requires_modal) {
        // Razorpay: open payment modal
        setPaymentStatus('modal_open')
        setApplicationToken(data.token)
        await openRazorpayModal(data.subscription_id, data.token, formData)
        return
      }

      // Fake mode: immediate success
      setPaymentStatus('success')
      setApplicationToken(data.token)
      submittingRef.current = false
    } catch {
      setPaymentStatus('error')
      setPaymentError('Network error. Please try again.')
      trackPaymentFailed(gateway, 'Network error')
      submittingRef.current = false
    }
  }, [gateways, razorpayKeyId])

  const openRazorpayModal = useCallback(async (
    subscriptionId: string,
    token: string,
    formData: FormData
  ) => {
    try {
      await loadRazorpaySdk()

      const options = {
        key: razorpayKeyId,
        subscription_id: subscriptionId,
        name: 'wavbee',
        description: 'Application Verification',
        prefill: {
          name: `${formData.personal_info.first_name} ${formData.personal_info.last_name}`,
          email: formData.personal_info.email,
          contact: `+${formData.personal_info.phone_dial_code}${formData.personal_info.phone}`
        },
        theme: { color: '#ff6a1a' },
        handler: async (response: { razorpay_payment_id: string; razorpay_subscription_id: string; razorpay_signature: string }) => {
          setPaymentStatus('verifying')
          try {
            const verifyRes = await fetch('/applications/verify_razorpay_payment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrfToken()
              },
              body: JSON.stringify({
                payment_id: response.razorpay_payment_id,
                subscription_id: response.razorpay_subscription_id,
                signature: response.razorpay_signature,
                application_token: token
              })
            })

            const verifyData = await verifyRes.json()

            if (verifyData.success) {
              setPaymentStatus('success')
              setApplicationToken(verifyData.token)
            } else {
              const message = verifyData.error || 'Payment verification failed'
              setPaymentStatus('error')
              setPaymentError(message)
              trackPaymentFailed('razorpay', message, token)
            }
          } catch {
            setPaymentStatus('error')
            setPaymentError('Payment verification failed. Please contact support.')
            trackPaymentFailed('razorpay', 'Verification error', token)
          }
          submittingRef.current = false
        },
        modal: {
          ondismiss: () => {
            // The applicant closed the gateway without paying. Tell the
            // server so the application is marked abandoned (not left looking
            // like a live awaiting sub), and surface clear retry guidance —
            // the application is saved but is NOT submitted until payment
            // clears.
            void fetch(`/applications/${encodeURIComponent(token)}/abandon_payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() }
            }).catch(() => { /* best-effort */ })
            setPaymentStatus('error')
            setPaymentError('Payment wasn’t completed, so your application isn’t submitted yet. Click Submit Application to try again.')
            trackPaymentFailed('razorpay', 'Modal dismissed', token)
            submittingRef.current = false
          }
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rzp = new (window as any).Razorpay(options)
      rzp.open()
    } catch {
      setPaymentStatus('error')
      setPaymentError('Failed to load payment gateway. Please try again.')
      trackPaymentFailed('razorpay', 'SDK load failed')
      submittingRef.current = false
    }
  }, [razorpayKeyId])

  const resetPayment = useCallback(() => {
    setPaymentStatus('idle')
    setPaymentError(null)
    submittingRef.current = false
  }, [])

  return { paymentStatus, paymentError, applicationToken, submitApplication, resetPayment }
}
